import { useState, type ReactNode } from "react";

import { UserSheetContext } from "./userSheetContext";
import type { UserSheetCommsTab } from "./types";

import { USER_SHEET_DEFAULT_COMMS_WIDTH_PERCENT } from "@/lib/constants";
import { userSheetViewStore } from "@/stores/userSheetView";

// Owns the sheet's identity scope and comms-rail state so UserSheetFrame,
// UserSheetBody, UserSheetHeader, and UserSheetTabs can be composed freely by
// each host. Always the outermost sheet block. View preferences (collapse,
// zoom, split panes) live in userSheetViewStore, reset here per sheet open.
export const UserSheetProvider = ({
  candidacyId,
  children,
  defaultCommunicationsOpen = false,
  hasCommunications = true,
  isReadOnly = false,
  jobId,
  onCommunicationsOpenChange,
  onEditProfile,
  workerId,
}: {
  candidacyId?: string;
  children: ReactNode;
  defaultCommunicationsOpen?: boolean;
  // False when the host mounts no comms panel — the header handle hides and
  // openCommunications no-ops rather than expanding an empty rail.
  hasCommunications?: boolean;
  // Suppresses every mutation entry point in the sheet — see UserSheetContext.
  isReadOnly?: boolean;
  jobId?: string;
  // Fires when the staffer opens/closes the comms rail — lets hosts persist
  // the preference (e.g. the search sheet remembers it for the session).
  onCommunicationsOpenChange?: (isOpen: boolean) => void;
  onEditProfile?: () => void;
  workerId: number;
}) => {
  /* React Hooks */
  // Reset view preferences before children first render, once per sheet open.
  useState(() => {
    userSheetViewStore.getState().reset();
    return null;
  });
  const [isCommunicationsOpen, setIsCommunicationsOpen] = useState(
    defaultCommunicationsOpen,
  );
  const [communicationsTab, setCommunicationsTab] =
    useState<UserSheetCommsTab>("messages");
  // Undefined until the staffer picks a scope — the panel applies its default.
  const [communicationsScope, setCommunicationsScope] = useState<string>();
  const [communicationsRequestCount, setCommunicationsRequestCount] =
    useState(0);
  // What the latest open asked for. Kept beside the counter so the panel reads
  // both from the same render, rather than racing two separate updates.
  const [communicationsRequestScope, setCommunicationsRequestScope] =
    useState<string>();
  const [communicationsWidthPercent, setCommunicationsWidthPercent] = useState(
    USER_SHEET_DEFAULT_COMMS_WIDTH_PERCENT,
  );

  /* Helpers */
  const contextValue = {
    candidacyId,
    closeCommunications: () => {
      setIsCommunicationsOpen(false);
      onCommunicationsOpenChange?.(false);
    },
    communicationsScope,
    communicationsRequestScope,
    communicationsTab,
    communicationsRequestCount,
    communicationsWidthPercent,
    contentPaddingClassName: isCommunicationsOpen ? "pl-11 pr-6" : "pl-7 pr-6",
    hasCommunications,
    isCommunicationsOpen,
    isReadOnly,
    jobId,
    onEditProfile,
    openCommunications: (tab: UserSheetCommsTab, scope?: string) => {
      if (!hasCommunications) return;
      setCommunicationsTab(tab);
      setCommunicationsRequestScope(scope);
      setIsCommunicationsOpen(true);
      setCommunicationsRequestCount((count) => count + 1);
      onCommunicationsOpenChange?.(true);
    },
    setCommunicationsScope,
    setCommunicationsWidthPercent,
    workerId,
  };

  /* Render */
  return (
    <UserSheetContext.Provider value={contextValue}>
      {children}
    </UserSheetContext.Provider>
  );
};
